import { toast } from "@/components/common/Toast"
import { PERMISSIONS, type PermissionKey } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { isLimitError } from "@/utils/limitErrors"
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import { apiErrorMessage, getApiErrorCode, getApiErrorStatus } from "./apiError"

// The router is mounted at /api/v2/mc/notes and apiClientV2's base URL already ends
// in /api/v2. Note this is NOT the /material-compliance prefix the other MC routers use.
const BASE = "/mc/notes"

// ─── Wire shapes (apiClientV2 converts snake_case → camelCase) ──────────────────

export type NoteEntityType = "inquiry" | "supplier"

export interface NoteAttachmentOut {
  id: string
  fileName: string
  /** image/png, image/jpeg or application/pdf, detected from the bytes server-side. */
  contentType: string
  sizeBytes: number
  // apiClientV2 revives createdAt/updatedAt keys into Date instances on response.
  createdAt: Date
  /** The backend stream route. It needs the bearer token, so never use it as a
   *  bare <img src> or link: fetch the bytes with `fetchNoteAttachment`. */
  url: string
}

export interface NoteOut {
  id: string
  entityType: NoteEntityType
  entityId: string
  body: string
  /** Only meaningful on a supplier note: it asks for a confirmation on every new send. */
  warnOnInquiry: boolean
  createdBy: string
  createdAt: Date
  updatedBy: string | null
  updatedAt: Date | null
  /** updated_at > created_at, computed server-side. */
  edited: boolean
  attachments: NoteAttachmentOut[]
}

// Limits the server enforces (and has the final word on): the client only pre-checks.
export const NOTE_MAX_FILES = 5
export const NOTE_MAX_FILE_BYTES = 10 * 1024 * 1024
export const NOTE_MAX_BODY_CHARS = 4000

/** The write permission per entity type: inquiry notes follow sending, supplier
 *  notes follow master-data editing. Reads need only the view keys. */
export const NOTE_WRITE_PERMISSION: Record<NoteEntityType, PermissionKey> = {
  inquiry: PERMISSIONS.MC_INQUIRIES_SEND,
  supplier: PERMISSIONS.MC_MASTER_DATA_EDIT,
}

// ─── Errors ─────────────────────────────────────────────────────────────────────

// Codes the notes routes answer with. Kept out of apiError.ts's shared table: that
// one is for copy that reads correctly at ANY call site, and "file too large" or
// "unsupported file type" there would be read as the declaration upload's.
const NOTE_ERROR_KEYS: Partial<Record<string, TranslationKeys>> = {
  FILE_TOO_LARGE: "MC_NOTE_ERR_FILE_TOO_LARGE",
  UNSUPPORTED_FILE_TYPE: "MC_NOTE_ERR_UNSUPPORTED_FILE",
  ATTACHMENT_LIMIT: "MC_NOTE_ERR_ATTACHMENT_LIMIT",
}

export function noteErrorMessage(
  error: unknown,
  translate: (key: TranslationKeys) => string,
  fallbackKey: TranslationKeys = "MC_NOTE_ERR_GENERIC",
): string {
  const key = NOTE_ERROR_KEYS[getApiErrorCode(error) ?? ""]
  return key ? translate(key) : apiErrorMessage(error, translate, fallbackKey)
}

/** Same rule as toastApiError: the axios interceptor already toasts every >= 500
 *  and every limit or trial error. */
export function toastNoteError(
  error: unknown,
  translate: (key: TranslationKeys) => string,
  fallbackKey: TranslationKeys = "MC_NOTE_ERR_GENERIC",
): void {
  const status = getApiErrorStatus(error)
  if (status !== null && status >= 500) return
  if (isLimitError(error)) return
  toast.error(noteErrorMessage(error, translate, fallbackKey))
}

// ─── Cache ──────────────────────────────────────────────────────────────────────

const NOTES_KEY = ["mc", "notes"] as const

/**
 * A note write changes more than its own list: the inquiry detail carries the
 * timeline and the supplier's warnings, and the supplier list and the resolved
 * selection (see useResolveSuppliers) carry the warning count. The detail is
 * invalidated by prefix because a warn toggle on a supplier note changes the
 * banner of EVERY inquiry of that supplier, not just an open one.
 */
export function invalidateNoteCaches(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: NOTES_KEY })
  qc.invalidateQueries({ queryKey: ["mc", "inquiries", "full-detail"] })
  qc.invalidateQueries({ queryKey: ["mc", "suppliers", "list"] })
  qc.invalidateQueries({ queryKey: ["mc", "suppliers", "resolve"] })
}

// ─── List (GET ?entity_type=&entity_id=) ────────────────────────────────────────

export function useNotes(entityType: NoteEntityType, entityId: string | null) {
  const enabled = useFeatureFlag("MC_NOTES")
  return useQuery({
    queryKey: [...NOTES_KEY, "list", entityType, entityId],
    queryFn: () =>
      apiClientV2
        .get<NoteOut[]>(BASE, {
          // params are not case-converted: the wire names are spelled out.
          params: { entity_type: entityType, entity_id: entityId },
        })
        .then((r) => (Array.isArray(r.data) ? r.data : [])),
    enabled: enabled && !!entityId,
  })
}

// ─── Writes ─────────────────────────────────────────────────────────────────────

export interface CreateNoteInput {
  entityType: NoteEntityType
  entityId: string
  body: string
  warnOnInquiry?: boolean
  files?: File[]
}

// A FormData body skips apiClientV2's case conversion, so the field names are the
// wire names. The files go under ONE repeated `files` field: that is what a
// FastAPI `files: list[UploadFile]` parameter binds ("files[]" would not).
function noteForm(
  fields: Record<string, string>,
  files: readonly File[] = [],
): FormData {
  const form = new FormData()
  for (const [name, value] of Object.entries(fields)) form.append(name, value)
  for (const file of files) form.append("files", file)
  return form
}

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } }

/** POST a note. A plain function so the close flow can await it and keep the id. */
export async function createNoteRequest(
  input: CreateNoteInput,
): Promise<NoteOut> {
  const form = noteForm(
    {
      entity_type: input.entityType,
      entity_id: input.entityId,
      body: input.body,
      warn_on_inquiry: String(input.warnOnInquiry ?? false),
    },
    input.files,
  )
  const { data } = await apiClientV2.post<NoteOut>(BASE, form, MULTIPART)
  return data
}

/**
 * The mutations take the entity type because one set of routes serves both, while
 * the permission differs: it picks which key guards the hook.
 */
function useNoteMutation<TVariables, TData>(
  entityType: NoteEntityType,
  mutationFn: (variables: TVariables) => Promise<TData>,
  // The copy for a failure the server gave no reason for: "could not be saved"
  // would be wrong for a removal.
  failureKey: TranslationKeys = "MC_NOTE_ERR_GENERIC",
) {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn,
    onSuccess: () => invalidateNoteCaches(qc),
    onError: (error) => toastNoteError(error, translate, failureKey),
  })
  return useGuardedMutation(mutation, NOTE_WRITE_PERMISSION[entityType])
}

export function useCreateNote(entityType: NoteEntityType) {
  return useNoteMutation(entityType, createNoteRequest)
}

export interface UpdateNoteInput {
  id: string
  body?: string
  warnOnInquiry?: boolean
}

export function useUpdateNote(entityType: NoteEntityType) {
  return useNoteMutation(entityType, ({ id, ...changes }: UpdateNoteInput) =>
    // JSON body: apiClientV2 snake-cases warnOnInquiry.
    apiClientV2.patch<NoteOut>(`${BASE}/${id}`, changes).then((r) => r.data),
  )
}

export function useRemoveNote(entityType: NoteEntityType) {
  return useNoteMutation(
    entityType,
    (id: string) => apiClientV2.delete(`${BASE}/${id}`).then(() => undefined),
    "MC_NOTE_ERR_REMOVE",
  )
}

export function useAddNoteAttachments(entityType: NoteEntityType) {
  return useNoteMutation(
    entityType,
    ({ id, files }: { id: string; files: File[] }) =>
      apiClientV2
        .post<NoteOut>(
          `${BASE}/${id}/attachments`,
          noteForm({}, files),
          MULTIPART,
        )
        .then((r) => r.data),
  )
}

export function useRemoveNoteAttachment(entityType: NoteEntityType) {
  return useNoteMutation(
    entityType,
    ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      apiClientV2
        .delete<NoteOut>(`${BASE}/${id}/attachments/${attachmentId}`)
        .then((r) => r.data),
    "MC_NOTE_ERR_REMOVE",
  )
}

// ─── Attachment bytes (GET /{note_id}/attachments/{attachment_id}) ──────────────

// `url` on an attachment is the whole path, /api/v2/mc/notes/{note}/attachments/{id},
// while apiClientV2's base URL already ends in /api/v2.
const API_V2_PREFIX = /^\/api\/v2(?=\/)/

/**
 * The stream route answers with the file itself, behind the bearer token. A browser
 * tab or <img> cannot send that header (and `url` is a path on the API host, not on
 * this origin), so the bytes are fetched here as a blob and shown through an object
 * URL, the same approach as downloadQuestionnaire.
 */
export function fetchNoteAttachment(
  attachment: Pick<NoteAttachmentOut, "url">,
): Promise<Blob> {
  return apiClientV2
    .get<Blob>(attachment.url.replace(API_V2_PREFIX, ""), {
      responseType: "blob",
    })
    .then((r) => r.data)
}

/** An attachment never changes once stored (removal is soft), so the blob is cached for good. */
export function useNoteAttachmentBlob(
  { id, url }: Pick<NoteAttachmentOut, "id" | "url">,
  enabled = true,
) {
  return useQuery({
    queryKey: ["mc", "note-attachment", id, url],
    queryFn: () => fetchNoteAttachment({ url }),
    enabled,
    staleTime: Infinity,
  })
}
